<template>
    <div id="contextMenu" ref="menu" :class="{hidden: !show}" :style="inlineStyle">
        <List v-if="!isMultiSelect">
            <ListItem :disabled="!canEditLeavePeriods" v-if="permissions[userPermissionsEnum.EditLeavePeriods] && isOnThisRoster" @click="enterLeavePeriod" label="Verlofperiode toevoegen" />
            <ListItem :disabled="!canEditLeavePeriods" v-if="permissions[userPermissionsEnum.EditLeavePeriods] && isOnThisRoster && resource && hasLeavePeriods" @click="removeLeavePeriod" label="Verlofperiode verwijderen" />
            <ListItem :disabled="!canEditDeploymentsOrStampShifts" v-if="allowedToEditDeploymentsOrStampShifts" @click="enterNewShift" label="Nieuwe dienst toevoegen" sublabel="Ctrl + d / &#8984; + d" />
            <ListItem :disabled="!canProposeDeployments" v-if="allowedToProposeDeployments" @click="enterNewDeploymentProposal" label="Nieuwe dienst voorstellen" />
            <ListItem :disabled="!canEditEmploymentComments" v-if="permissions[userPermissionsEnum.EditEmploymentComment] && isOnThisRoster" @click="openCommentEditor" label="Opmerking toevoegen/bewerken" />
            <ListItem :disabled="!canDeleteAllDeployments" v-if="allowedToDeleteAllDeployments && !isInStampPeriod" @click="openDeploymentsStampsModal('DEPLOYMENTS')" label="Alle diensten vanaf deze dag verwijderen" />
            <ListItem v-if="day && isOnThisRoster" @click="openDeploymentsHistoryModal" label="Geschiedenis" />
            <ListItem :disabled="!canEditWishes" v-if="permissions[userPermissionsEnum.EditWishes] && isOnThisRoster" @click="openWishes" label="Wensen bewerken" />
            <ListItem v-if="canCreateStampIteration" @click="enterStampIteration" label="Nieuwe stempel met deze datum als ingangsdatum" />
            <ListItem :disabled="!canCopyStamp" v-if="permissions[userPermissionsEnum.EditStampPeriod] && isOnThisRoster && hasStamp" @click="copyStamp" label="Stempel kopieren" />
            <ListItem v-if="permissions[userPermissionsEnum.EditStampPeriod] && isOnThisRoster && hasStamp && stampOnClipboard" @click="pasteStamp" label="Stempel plakken" />
            <ListItem :disabled="!stampsHasSameLength" v-if="permissions[userPermissionsEnum.EditStampPeriod] && isOnThisRoster && hasStamp && stampOnClipboard" @click="pasteStampRotating" label="Stempel roulerend plakken" />
            <ListItem :disabled="!canDeleteAllDeployments || !hasStamp" v-if="canDeleteAllDeployments && isInStampPeriod" @click="openDeploymentsStampsModal('STAMPS')" label="Stempel vanaf deze datum verwijderen" />
            <ListItem v-if="allowedToEditDeploymentsOrStampShifts && allowAssignedDays" @click="openAssignedDay" label="Dagtype toewijzen" />
            <template v-if="!oldHrCouplingEnabled && resource && permissions[userPermissionsEnum.EditSicknessCases] && isOnThisRoster">
                <template v-if="hasSickPeriod">
                    <ListItem :label="sicknessPeriodLabel + ' bewerken'" @click="editSickPeriods" />
                    <ListItem :label="sicknessPeriodLabel + ' verwijderen'" @click="deleteSickPeriods" />
                </template>
                <ListItem v-else label="Ziekteperiode toevoegen" @click="createSickPeriod" />
            </template>
        </List>
        <List v-else>
            <ListItem
                :disabled="!allowedToEditAllSelectedDeploymentsOrStampShifts || deploymentStateCells.length === 0"
                withSubmenu
                label="Pas toestand aan"
                class="sub-menu"
            >
                <DeploymentCellContextMenuState
                    :shifts="shifts"
                    :organisation="organisation"
                    :cells="deploymentStateCells"
                    @update:deploymentState="emitDeploymentStateChange"
                />
            </ListItem>
        </List>
    </div>
</template>

<script lang="ts">
    import List from '@/components/Branding/List.vue';
    import ListItem from '@/components/Branding/ListItem.vue';
    import Notify from '../modals/Notify.vue';
    import Wishes from '@/components/modals/Wishes.vue';
    import LeavePeriodModal from '../modals/LeavePeriodModal.vue';
    import ShiftStateUpdateModal from '@/components/modals/ShiftStateUpdateModal.vue';
    import PersonalStampIterationModal from '@/components/modals/PersonalStampIterationModal.vue';
    import AssignedDaysModal from '@/components/modals/AssignedDaysModal.vue';
    import SickPeriodEditModal from '@/components/modals/SickPeriodEditModal.vue';
    import DeploymentsStampsDeleteModal from '@/components/modals/DeploymentsStampsDeleteModal.vue';
    import StampShiftEditModal from '@/components/modals/StampShiftEditModal.vue';
    import EmploymentCommentModal from '@/components/modals/EmploymentCommentModal.vue';
    import DeploymentCellContextMenuState from '@/components/Planbord/DeploymentCellContextMenuState.vue';
    import DeploymentHistoryModal from '@/components/modals/DeploymentHistoryModal.vue';
    import {RosterDay, RosterData, PermissionsForRoster} from '@/types/types';
    import {ApiClient, LeavePeriod, PersonalStamp, SickPeriod, PersonalStampShift, AssignedDay, Deployment, DeploymentSpecification, Organisation, Shift, Company, RosterDayType, DeploymentStateNumber} from '@cquential/api';
    import {ResourceWithKeyedByDateObjects, PlanbordResource, CompanyWithDeploymentsAndEmployees, CompanyEmployeeWithDeployments} from '@/types/api-object-extensions';
    import Dates from '@cquential/date-utils';
    import cloneDeep from 'lodash/cloneDeep';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import {Component, Vue, Prop, Ref} from 'vue-facing-decorator';
    import {ContextMenuData} from '@/components/types';
    import PlanbordCell from '@/components/Planbord/PlanbordCell.vue';
    import CellCapacity from '@/util/CellCapacity';

    @Component({
        components: {
            List,
            ListItem,
            DeploymentCellContextMenuState,
        },
        emits: [
            'delete:leavePeriod',
            'create:stampShifts',
            'delete:sickPeriod',
            'update:deployments',
        ],
    })
    export default class DeploymentCellContextMenu extends Vue {
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public shifts!: Record<number, Shift>;
        @Prop({required: true}) public defaultShiftIdPerRoster!: Record<number, number>;
        @Prop({required: true}) public permissions!: PermissionsForRoster;
        @Prop({required: true}) public organisation!: Organisation;
        @Prop({type: Boolean}) public hrCouplingEnabled!: boolean;
        @Prop({type: Boolean}) public oldHrCouplingEnabled!: boolean;
        @Prop({type: String}) public hrCouplingType?: string;

        @Ref() public readonly menu!: HTMLDivElement;

        private day: RosterDay | null = null;
        private company: CompanyWithDeploymentsAndEmployees | null = null;
        private companyEmployee: CompanyEmployeeWithDeployments | null = null;
        private resource: ResourceWithKeyedByDateObjects | null = null;
        private leavePeriods: LeavePeriod[] | null = null;
        private stamp: PersonalStamp | null = null;
        private hasStamp: boolean = false;
        private stampOnClipboard: PersonalStamp | null = null;
        private stampStart: boolean = false;
        private stampEnd: boolean = false;
        private sickPeriods: SickPeriod[] = [];
        private show: boolean = false;
        private isMultiSelect: boolean = false;
        private multiSelectTargets: PlanbordCell[] = [];
        private isOnThisRoster: boolean = true;

        private top: number | null = 0;
        private bottom: number | null = null;
        private left: number = 0;

        private static instance: DeploymentCellContextMenu | null;

        // direct enum access doesn't work in template
        public get userPermissionsEnum(): typeof UserPermissions {
            return UserPermissions;
        }

        public get canEditLeavePeriods(): boolean {
            return this.isOnThisRoster && this.permissions[UserPermissions.EditLeavePeriods] && this.isEmployed && this.resource !== null && !!this.resource.id;
        }

        public get canEditEmploymentComments(): boolean {
            return this.isOnThisRoster && this.permissions[UserPermissions.EditEmploymentComment] && this.resource !== null && !!this.resource.employment;
        }

        public get canEditWishes(): boolean {
            return this.isOnThisRoster && this.permissions[UserPermissions.EditWishes] && this.day !== null && this.resource !== null && !!this.resource.id;
        }

        public get allowedToEditDeploymentsOrStampShifts(): boolean {
            if (!this.day || !this.isOnThisRoster) {
                return false;
            }

            if (this.day.proposed) {
                return this.permissions[UserPermissions.EditPublishedPeriod];
            }

            if (this.isInStampPeriod) {
                return this.permissions[UserPermissions.EditStampPeriod];
            }

            return this.permissions[UserPermissions.EditConstructionPeriod];
        }

        public get allowedToProposeDeployments(): boolean {
            if (this.allowedToEditDeploymentsOrStampShifts) {
                // well... you could still be allowed, but there is no point
                return false;
            }

            if (this.resource === null) {
                return false;
            }

            if (!this.day) {
                return false;
            }

            return this.permissions[this.day.proposed ? UserPermissions.ProposeDeploymentsPublishedPeriod : UserPermissions.ProposeDeploymentsConstructionPeriod];
        }

        public get allowedToEditAllSelectedDeploymentsOrStampShifts(): boolean {
            for (const deploymentCell of this.deploymentStateCells) {
                if (!deploymentCell.isOnThisRoster) {
                    return false;
                }

                if (deploymentCell.day.proposed && !this.permissions[UserPermissions.EditPublishedPeriod]) {
                    return false;
                }

                // stamps don't matter, we don't change their states

                if (!this.permissions[UserPermissions.EditConstructionPeriod]) {
                    return false;
                }
            }

            return true;
        }

        public get canEditDeploymentsOrStampShifts(): boolean {
            return this.allowedToEditDeploymentsOrStampShifts && this.isEmployed && (!this.isInStampPeriod || !!this.stamp);
        }

        public get canProposeDeployments(): boolean {
            return this.allowedToProposeDeployments && this.isEmployed && (!this.isInStampPeriod || !!this.stamp);
        }

        public get inlineStyle(): Record<string, string | number> {
            const style: Record<string, string | number> = {};

            style.left = this.left + 'px';
            if (this.top !== null) {
                style.top = this.top + 'px';
                style.maxHeight = 'calc(100vh - ' + this.top + 'px)'; // make it fit in the window
            }
            if (this.bottom !== null) {
                style.bottom = this.bottom + 'px';
                style.maxHeight = 'calc(100vh - ' + this.bottom + 'px)'; // attempt to prevent the menu from overflowing the body at the top
            }

            return style;
        }

        public get hasLeavePeriods(): boolean {
            return !!this.leavePeriods && this.leavePeriods.length > 0;
        }

        public get isInStampPeriod(): boolean {
            return this.resource !== null && (this.resource.stampDeployedUntil === null || (!!this.day && this.day.date > this.resource.stampDeployedUntil));
        }

        public get allowedToDeleteAllDeployments(): boolean {
            if (!this.day) {
                return false;
            }

            if (!this.isOnThisRoster) {
                return false;
            }

            if (this.day.proposed && !this.permissions[UserPermissions.EditPublishedPeriod]) {
                return false;
            }

            if (!this.isInStampPeriod && !this.permissions[UserPermissions.EditConstructionPeriod]) {
                return false;
            }

            return this.permissions[UserPermissions.EditStampPeriod];
        }

        public get canDeleteAllDeployments(): boolean {
            if (this.resource === null) {
                return false;
            }

            if (!this.day) {
                return false;
            }

            if (!this.isOnThisRoster) {
                return false;
            }

            if (this.day.date < this.resource.salaryAcceptedUntil) {
                return false;
            }

            return this.allowedToDeleteAllDeployments;
        }

        public get isEmployed(): boolean {
            if (!this.day) {
                return true;
            }

            return CellCapacity.mayContainDeployments(this.day.date, this.resource, this.company, this.companyEmployee);
        }

        public get allowAssignedDays(): boolean {
            return this.organisation.hasAssignedDays && this.resource !== null && (!this.isInStampPeriod || this.stamp !== null);
        }

        public get hasSickPeriod(): boolean {
            return this.sickPeriods.length > 0;
        }

        public get sicknessPeriodLabel(): string {
            return this.sickPeriods.length === 1 ? 'Ziekteperiode' : 'Ziekteperiodes';
        }

        public get canCopyStamp(): boolean {
            return !!this.stamp
                && this.stamp.personalStampShifts !== undefined
                && this.stamp.personalStampShifts.length > 0;
        }

        public get stampsHasSameLength(): boolean {
            return !!this.stamp
                && !!this.stampOnClipboard
                && this.stamp.iterationLength === this.stampOnClipboard.iterationLength;
        }

        public get canCreateStampIteration(): boolean {
            return this.permissions[UserPermissions.EditStampPeriod]
                && this.isInStampPeriod
                && !this.stampStart
                && this.isEmployed
                && this.isOnThisRoster;
        }

        public get deploymentStateCells(): PlanbordCell[] {
            return this.multiSelectTargets.filter((cell: PlanbordCell): boolean => {
                return !cell.isInStampPeriod;
            });
        }

        public mounted(): void {
            DeploymentCellContextMenu.instance = this;
            document.body.addEventListener('click', this.hideMenu); // hide the menu when anything outside of it is clicked
            document.body.addEventListener('contextmenu', this.hideMenu); // hide the menu whenever anything outside of it is right clicked
            window.addEventListener('blur', this.hideMenu); // hide the menu whenever you blur the browser window/tab
            window.addEventListener('resize', this.hideMenu); // we could try to force it to fit, but if you're resizing the window, you don't care about the menu anymore, do you?
        }

        public beforeUnmount(): void {
            DeploymentCellContextMenu.instance = null;
            document.body.removeEventListener('click', this.hideMenu);
            document.body.removeEventListener('contextmenu', this.hideMenu);
            window.removeEventListener('blur', this.hideMenu);
            window.removeEventListener('resize', this.hideMenu);
        }

        public showMenu(x: number, y: number, data: ContextMenuData): void {
            this.show = true;

            this.day = data.day;
            this.company = data.company ? data.company : null;
            this.companyEmployee = data.companyEmployee ? data.companyEmployee : null;
            this.resource = data.resource ? data.resource : null;
            this.leavePeriods = data.leavePeriods;
            this.stamp = data.stamp ? data.stamp : null;
            this.hasStamp = data.hasStamp;
            this.stampStart = data.stampStart;
            this.stampEnd = data.stampEnd;
            this.sickPeriods = data.sickPeriods;
            this.isMultiSelect = data.isMultiSelect ?? false;
            this.multiSelectTargets = data.multiSelectTargets ?? [];
            this.isOnThisRoster = data.isOnCurrentRoster;

            this.setPosition(x, y);
        }

        public hideMenu(): void {
            this.show = false;
        }

        private setPosition(x: number, y: number): void {
            if ((y + 300) > window.innerHeight) {
                this.top = null;
                this.bottom = window.innerHeight - y;
            } else {
                this.bottom = null;
                this.top = y;
            }

            this.left = Math.min(x, window.innerWidth - 270);
        }

        public enterLeavePeriod(): void {
            if (!this.canEditLeavePeriods || this.resource === null || !this.isEmployed) {
                return;
            }

            /** @see LeavePeriodModal.open() */
            // @ts-expect-error TS2339
            LeavePeriodModal.__vfdConstructor.open(this.resource, this.day!.date);
        }

        public removeLeavePeriod(): void {
            if (!this.canEditLeavePeriods || !this.hasLeavePeriods || this.resource === null) {
                return;
            }

            for (const leavePeriod of this.leavePeriods!) {
                ApiClient.instance.deleteLeavePeriod(leavePeriod).then((): void => {
                    this.$emit('delete:leavePeriod', leavePeriod);
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De verlofperiode is verwijderd.', type: 'success'});
                }).catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'De verlofperiode kon niet verwijderd worden. Probeer het opnieuw.', type: 'error'});
                });
            }
        }

        public openCommentEditor(): void {
            if (this.canEditEmploymentComments) {
                /** @see EmploymentCommentModal.openModal() */
                // @ts-expect-error TS2339
                EmploymentCommentModal.__vfdConstructor.openModal(this.resource!.employment!, this.day!.date);
            }
        }

        public enterNewShift(): void {
            if (!this.allowedToEditDeploymentsOrStampShifts || !this.isEmployed) {
                return;
            }

            if (this.isInStampPeriod) {
                if (this.stamp) {
                    /** @see StampShiftEditModal.openModal() */
                    // @ts-expect-error TS2339
                    StampShiftEditModal.__vfdConstructor.openModal(this.resource!, this.stamp, null, this.day!.date);
                }
            } else {
                const shiftResource: ResourceWithKeyedByDateObjects | undefined = this.resource !== null ? this.resource : undefined;
                const shiftCompany: Company | undefined = (this.company) ? this.company : undefined;
                /** @see ShiftStateUpdateModal.openModal() */
                // @ts-expect-error TS2339
                ShiftStateUpdateModal.__vfdConstructor.openModal(shiftResource, shiftCompany, false, new Date(this.day!.date));
            }
        }

        public enterNewDeploymentProposal(): void {
            if (!this.allowedToProposeDeployments || !this.isEmployed) {
                return;
            }

            if (this.isInStampPeriod) {
                return;
            } else {
                const shiftResource: ResourceWithKeyedByDateObjects | undefined = this.resource !== null ? this.resource : undefined;
                /** @see ShiftStateUpdateModal.openModal() */
                // @ts-expect-error TS2339
                ShiftStateUpdateModal.__vfdConstructor.openModal(shiftResource, undefined, false, new Date(this.day!.date), true);
            }
        }

        public enterStampIteration(): void {
            if (!this.canCreateStampIteration) {
                return;
            }
            /** @see PersonalStampIterationModal.open() */
            // @ts-expect-error TS2339
            PersonalStampIterationModal.__vfdConstructor.open(this.resource!, this.day!.date);
        }

        public copyStamp(): void {
            if (this.permissions[UserPermissions.EditStampPeriod]) {
                this.stampOnClipboard = this.stamp;
            }
        }

        public pasteStamp(): void {
            if (this.permissions[UserPermissions.EditStampPeriod] && !!this.stamp && !!this.stampOnClipboard) {
                this.addShiftsFromClipboardToStamp(false);
            }
        }

        public pasteStampRotating(): void {
            if (this.permissions[UserPermissions.EditStampPeriod] && this.stampsHasSameLength) {
                this.addShiftsFromClipboardToStamp(true);
            }
        }

        private addShiftsFromClipboardToStamp(pasteRotating: boolean): void {
            if (this.stampOnClipboard
                && this.stampOnClipboard.personalStampShifts
                && this.stamp
                && this.stamp.id
                && this.day
            ) {
                const personalStampShifts: PersonalStampShift[] = [];

                const stampDayIndexToStart: number = Dates.Utils.diffInDays(this.day.date, this.stampOnClipboard.beginDate) % this.stampOnClipboard.iterationLength;
                for (const personalStampShift of this.stampOnClipboard.personalStampShifts) {
                    let newDayIndex: number = personalStampShift.dayIndex + stampDayIndexToStart;
                    if (newDayIndex >= this.stamp.iterationLength) {
                        if (!pasteRotating) {
                            continue;
                        }
                        newDayIndex = (newDayIndex - this.stamp.iterationLength);
                    }
                    const newPersonalStampShift: PersonalStampShift = {
                        stampId: this.stamp.id,
                        dayIndex: newDayIndex,
                        shiftId: personalStampShift.shiftId,
                    };
                    personalStampShifts.push(newPersonalStampShift);
                }
                this.$emit('create:stampShifts', {shifts: personalStampShifts, resourceIds: this.resource?.id ? [this.resource.id] : []});
            }
        }

        public openWishes(): void {
            if (this.canEditWishes) {
                /** @see Wishes.openWishes() */
                // @ts-expect-error TS2339
                Wishes.__vfdConstructor.openWishes(this.day!.date, this.resource as PlanbordResource);
            } else {
                throw new Error('Current user cannot edit wishes.');
            }
        }

        public openDeploymentsHistoryModal(): void {
            if (!this.day) {
                return;
            }

            /** @see DeploymentHistoryModal.openModal() */
            // @ts-expect-error TS2339
            DeploymentHistoryModal.__vfdConstructor.openModal(this.day.date, this.resource, this.company, this.companyEmployee, this.allowedToEditDeploymentsOrStampShifts);
        }

        public openDeploymentsStampsModal(type: 'DEPLOYMENTS' | 'STAMPS'): void {
            if (!this.canDeleteAllDeployments) {
                return;
            }

            const deployments: boolean = type === 'DEPLOYMENTS';
            const stamps: boolean = type === 'STAMPS';

            if (stamps && !this.hasStamp) {
                return;
            }

            /** @see DeploymentsStampsDeleteModal.open() */
            // @ts-expect-error TS2339
            DeploymentsStampsDeleteModal.__vfdConstructor.open(
                this.resource!,
                this.roster,
                this.day!.date,
                deployments,
                stamps,
                this.canDeleteAllDeployments,
            );
        }

        public openAssignedDay(): void {
            if (this.isInStampPeriod) {
                this.openStampAssignedDay();
            } else {
                this.openNonStampAssignedDay();
            }
        }

        private openStampAssignedDay(): void {
            const dayOfWeek: number = Dates.Utils.getDay(this.day!.date);
            const dayIndex: number = Dates.Utils.diffInDays(this.day!.date, this.stamp!.beginDate) % this.stamp!.iterationLength;

            const assignedDay: AssignedDay = {
                date: new Date(this.day!.date),
                resourceId: this.resource!.id!,
                type: dayOfWeek == Dates.Utils.SUNDAY ? RosterDayType.Sunday : (dayOfWeek == Dates.Utils.SATURDAY ? RosterDayType.Saturday : RosterDayType.Normal),
            };

            for (const stampAssignedDay of this.stamp!.personalStampAssignedDays!) {
                if (stampAssignedDay.dayIndex === dayIndex) {
                    assignedDay.id = stampAssignedDay.id;
                    assignedDay.type = stampAssignedDay.type;
                    break;
                }
            }

            /** @see AssignedDaysModal.open() */
            // @ts-expect-error TS2339
            AssignedDaysModal.__vfdConstructor.open(assignedDay, true, this.stamp);
        }

        private openNonStampAssignedDay(): void {
            let assignedDay: AssignedDay;
            const date: string = this.day!.formattedDateIso;
            if (date in this.resource!.assignedDays! && this.resource!.assignedDays![date].length > 0) {
                assignedDay = {...this.resource!.assignedDays![date][0]};
            } else {
                const dayOfWeek: number = Dates.Utils.getDay(this.day!.date);

                assignedDay = {
                    date: new Date(this.day!.date),
                    resourceId: this.resource!.id!,
                    type: dayOfWeek == Dates.Utils.SUNDAY ? RosterDayType.Sunday : (dayOfWeek == Dates.Utils.SATURDAY ? RosterDayType.Saturday : RosterDayType.Normal),
                };
            }
            /** @see AssignedDaysModal.open() */
            // @ts-expect-error TS2339
            AssignedDaysModal.__vfdConstructor.open(assignedDay, false, null);
        }

        public createSickPeriod(): void {
            this.hideMenu();
            const now: Date = new Date();
            this.openSickPeriodEditor([{
                beginDate: this.day!.date,
                creationDate: now,
                resourceId: this.resource!.id!,
                percentages: [{
                    beginDate: this.day!.date,
                    percentage: 100,
                    creationDate: now,
                }],
            }]);
        }

        public editSickPeriods(): void {
            this.hideMenu();
            this.openSickPeriodEditor(this.sickPeriods);
        }

        private openSickPeriodEditor(sickPeriods: SickPeriod[]): void {
            if (!this.day) {
                return;
            }

            /** @see SickPeriodEditModal.open() */
            // @ts-expect-error TS2339
            SickPeriodEditModal.__vfdConstructor.open(this.day.date, cloneDeep(sickPeriods));
        }

        public deleteSickPeriods(): void {
            for (const sickPeriod of this.sickPeriods) {
                if ('id' in sickPeriod) {
                    ApiClient.instance.deleteSickPeriod(sickPeriod.id!).then((): void => {
                        this.$emit('delete:sickPeriod', sickPeriod);
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'De ziekteperiode is verwijderd.', type: 'success'});
                    }).catch((): void => {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'De ziekteperiode kon niet verwijderd worden. Probeer het opnieuw.', type: 'error'});
                    });
                } else {
                    this.$emit('delete:sickPeriod', sickPeriod);
                }
            }
        }

        public emitDeploymentStateChange(stateId: number): void {
            const deploymentsToSave: Deployment[] = [];
            const deployments: Deployment[] = this.deploymentStateCells.map((cell: PlanbordCell): Deployment[] => {
                if (cell.deployments.length === 0) {
                    if (!(this.roster.rosterId in this.defaultShiftIdPerRoster) || stateId === DeploymentStateNumber.Normal) {
                        return [];
                    }

                    return [{
                        shiftId: this.defaultShiftIdPerRoster[this.roster.rosterId],
                        date: cell.day.date,
                        resourceId: cell.resource?.id ?? null,
                        companyId: cell.company?.id ?? null,
                        companyEmployeeId: cell.companyEmployee?.id ?? null,
                        stateId: stateId,
                        publicComment: null,
                        specified: false,
                    }];
                }

                return cell.deployments;
            }).flat();

            for (const deployment of deployments) {
                let publicComment: string | null = null;
                if (this.organisation) {
                    for (const stateNumber in this.organisation.organisationDeploymentStates) {
                        if (stateNumber === stateId.toString()) {
                            publicComment = this.organisation.organisationDeploymentStates[stateNumber].comment;
                            break;
                        }
                    }
                }
                deploymentsToSave.push({
                    ...deployment,
                    stateId: stateId,
                    specifications: deployment.specifications?.map((specification: DeploymentSpecification): DeploymentSpecification => {
                        return {
                            ...specification,
                            stateId: stateId,
                        };
                    }),
                    publicComment: publicComment,
                });
            }
            this.$emit('update:deployments', deploymentsToSave);
            this.show = false;
        }

        public static showMenu(x: number, y: number, data: ContextMenuData): void {
            this.instance!.showMenu(x, y, data);
        }

        public static hideMenu(): void {
            this.instance!.hideMenu();
        }
    }
</script>

<style scoped lang="scss">
    #contextMenu {
        position: absolute;
        width: 250px;
        background: #fff;
        z-index: 2100;

        &.hidden {
            display: none;
        }

        .sub-menu {
            position: relative;

            .list {
                display: none;
                position: absolute;
                left: calc(100% - .25rem);
                top: 0;
            }

            &:hover .list {
                display: block;
            }
        }
    }
</style>
